import React, { useState } from 'react';
import {
  Stack,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';

import 'handsontable/dist/handsontable.full.min.css';
import { registerAllModules } from 'handsontable/registry';
import { useLocalization } from 'src/context/LocalizationContext';
import DhlReportList from './Views/FastReportListPages/DHLReportList';
import ArasReportList from './Views/FastReportListPages/ArasReportList';
import TaxFreeReportList from './Views/FastReportListPages/TaxFreeReportList';
import EticaretReportList from './Views/FastReportListPages/EticaretReportList';
import * as XLSX from 'xlsx';
import Services from 'src/services/Services';
import moment from 'moment';
import ExportToExcelButton from 'src/components/shared/ExportToExcelButton';

registerAllModules();

export default function FastReportList() {
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const { language } = useLocalization();
  const [selectedTab, setSelectedTab] = useState("E-TİCARET");

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };
  const fDate = (ts) => {
    if (!ts) return "-";
    const d = new Date(ts * 1000);
    const y = d.getFullYear();
    if (y === 1970) return "-";
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}.${y}`;
  };

  // Her raporun tablo-export’unda kullandığın kolon düzenlerine sadık kalarak map’ler:
  const mapDHL = (list = []) => list.map((x, i) => ({
    "No": i + 1,
    "İşlem Tarihi": fDate(x.operationDate),
    "Konşimento Numarası (Waybill)": x.waybill,
    "Gönderen Adı": x.senderName,
    "Alıcı Adı": x.receiverName,
    "İşlem Yapan EP Çalışanı": x.processingStaff,
    "Teslim Eden EP Çalışanı": x.deliveredStaff || "-",
    "Teslim Tarihi": fDate(x.deliveredDate),
    "Tutar (TL)": x.price || "-",
    "Lokasyon": x.poiTitle,
    "Açıklama": x.description,
    "Not": x.additionalNote || "-"
  }));

  const mapARAS = (list = []) => list.map((x, i) => ({
    "No": i + 1,
    "İşlem Tarihi": fDate(x.inBranchDate),
    "Gönderi No": x.barcode,
    "Müşteri Ad Soyad": x.customerName,
    "EP Personel Ad Soyadı": x.staff,
    "Açıklama": x.description || "-",
    "İşlem Türü": x.processingType,
    "Tutar": x.price || "-",
    "Ödeme Türü": x.paymentType,
    "İşlem Detay": x.processingDetails,
    "Lokasyon": x.poiTitle,
  }));

  const mapETICARET = (list = []) => list.map((x, i) => ({
    "No": i + 1,
    "E-Ticaret Markası": x.eCommerceBrand || "-",
    "Kargo Geliş Tarihi": x.inBranchDate ? fDate(x.inBranchDate) : "-",
    "Barkod Numarası": x.barcode || "-",
    "Pre-Printed Barkod": x.preprintedBarcode || "-",
    "İade Kodu": x.returnBarcode || "-",
    "Alıcı Müşteri Adı": x.receiverName || "-",
    "Teslim Alan Müşteri Adı": x.takenByName || "-",
    "Teslim Eden EP Çalışanı": x.deliveredStaff || "-",
    "Lokasyon": x.poiTitle || "-",
    "Müşteriye Teslim Tarihi": x.deliveredDate ? fDate(x.deliveredDate) : "-",
    "İşlem Türü": x.processingType || "-",
    "İade Tarihi": x.returnDate ? fDate(x.returnDate) : "-"
  }));

  const mapTAXFREE = (list = []) => list.map((x, i) => ({
    "No": i + 1,
    "Marka": x.brand,
    "İşlem Tarihi": fDate(x.operationDate),
    "Müşteri Fatura No": x.customerInvoiceNo,
    "Müşteri İsmi": x.customerName,
    "Ülke": x.country,
    "Fatura Tutarı": x.price,
    "Ödenen İade Tutarı": x.returnPrice,
    "Ödeme Türü": x.paymentType,
    "Mağaza": x.store,
    "Lokasyon": x.poiTitle,
    "EP Çalışanı": x.staff,
    "Mağaza Lokasyonu": x.storeLocation,
    "Not": x.additionalNote || "-"
  }));

  const exportAllToExcel = async () => {
    const token = parsedUser?.token;
    // Çocuk bileşenlerdeki servislerle aynı uçlar:
    const [dhlRes, arasRes, ecomRes, taxRes] = await Promise.all([
      Services.GetOperationReport(token, "dhl"),
      Services.GetOperationReport(token, "aras-kargo"),
      Services.GetOperationReport(token, "e-commerce"),
      Services.GetOperationReport(token, "tax-free"),
    ]);

    const dhl = dhlRes?.data?.result || [];
    const aras = arasRes?.data?.result || [];
    const eticaret = ecomRes?.data?.result || [];
    const taxfree = taxRes?.data?.result || [];

    const wb = XLSX.utils.book_new();

    const addSheet = (title, rows) => {
      // Header sırasını korumak için AOA üretelim:
      const headers = rows.length ? Object.keys(rows[0]) : [];
      const aoa = [headers, ...rows.map(r => headers.map(h => r[h]))];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      XLSX.utils.book_append_sheet(wb, ws, title);
    };

    addSheet("DHL Rapor", mapDHL(dhl));
    addSheet("Aras Rapor", mapARAS(aras));
    addSheet("E-Ticaret Rapor", mapETICARET(eticaret));
    addSheet("Tax Free Rapor", mapTAXFREE(taxfree));

    const fileName = `SahaRaporuListesi_${moment().format("YYYYMMDD_HHmm")}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const tabsComponent = (
    <Tabs
      value={selectedTab}
      onChange={handleTabChange}
      aria-label="Kargo Seçimi"
      sx={{ borderBottom: 1, borderColor: 'divider' }}
    >
      <Tab label="E-TİCARET" value="E-TİCARET" />
      <Tab label="DHL" value="DHL" />
      <Tab label="TAX FREE" value="TAX FREE" />
      <Tab label="ARAS" value="ARAS" />
    </Tabs>
  );

  return (
    <>
      <Helmet>
        <title>{language.fast_report_list.titles.site_title}</title>
      </Helmet>
      <Stack style={{ paddingInline: '25px' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            {language.fast_report_list.titles.title}
          </Typography>
          <div style={{display:'flex',flexDirection:'row',}}>
            <ExportToExcelButton onClick={exportAllToExcel} label="Tümünü Excel’e Aktar" />
          </div>
        </Stack>

        {selectedTab === 'DHL' && (
          <DhlReportList parsedUser={parsedUser} tabs={tabsComponent} />
        )}
        {selectedTab === 'TAX FREE' && (
          <TaxFreeReportList parsedUser={parsedUser} tabs={tabsComponent} />
        )}
        {selectedTab === 'ARAS' && (
          <ArasReportList parsedUser={parsedUser} tabs={tabsComponent} />
        )}
        {selectedTab === 'E-TİCARET' && (
          <EticaretReportList parsedUser={parsedUser} tabs={tabsComponent} />
        )}
      </Stack>
    </>
  );
}
